/**
 * TasksHub — production Tasks page (replaces /tasks).
 *
 * Layout (Salesflare-inspired but native to our slate-900 system):
 *   • Header: title + count + Today/Upcoming/Completed segmented control + search/filter.
 *   • Quick-add composer.
 *   • "My tasks" card — pulls from /api/tasks scoped by selected tab.
 *   • "Suggested tasks" card — pulls from /api/tasks-hub/suggested, mixing
 *     follow-ups-due / unreplied-inbound / idle-leads. ✓ accept / ✕ dismiss
 *     / 💤 snooze per row + Accept all batch.
 *
 * Phase 3 polish: keyboard shortcuts (J/K/D/S/R), URL-synced tab via ?tab=,
 *   virtualised list when > 100 rows.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Bell, UserPlus, CheckCircle2, Mail, AlertCircle,
  Reply, MoreVertical, Search, Sparkles, Clock, Building2, X,
} from 'lucide-react';
import ComposeModal from '@/components/email/ComposeModal';
import { useAuth } from '@/App';
import { API } from '@/App';
import { TabStrip } from '@/components/ui/native';

// ─── Avatar tone helper (deterministic) ─────────────────────────────────
const _AV_TONES = [
  'bg-amber-400 text-white', 'bg-rose-400 text-white', 'bg-emerald-400 text-white',
  'bg-sky-400 text-white',   'bg-indigo-400 text-white', 'bg-purple-400 text-white',
  'bg-orange-400 text-white', 'bg-teal-400 text-white',
];
function _avatarTone(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h << 5) - h + seed.charCodeAt(i);
  return _AV_TONES[Math.abs(h) % _AV_TONES.length];
}
function Avatar({ name, size = 28 }) {
  const initial = (name || '?').replace(/[^a-zA-Z]/g, '').slice(0, 1).toUpperCase() || '?';
  const px = size + 'px';
  return (
    <span className={`inline-flex items-center justify-center rounded-full font-semibold flex-shrink-0 ${_avatarTone(name)}`}
          style={{ width: px, height: px, fontSize: Math.round(size * 0.42) }}>
      {initial}
    </span>
  );
}

const TIME_TABS = [
  { id: 'today',     label: 'Today',     icon: Clock },
  { id: 'upcoming',  label: 'Upcoming',  icon: Clock },
  { id: 'completed', label: 'Completed', icon: CheckCircle2 },
];

function todayBounds() {
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const end = new Date(start); end.setDate(end.getDate() + 1);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}

// ─── Quick-add composer ─────────────────────────────────────────────────
function QuickAddBar({ onCreate }) {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  // ─── Related Account (Building icon) ─────────────────────────────────
  const [accOpen, setAccOpen] = useState(false);
  const [accQuery, setAccQuery] = useState('');
  const [accResults, setAccResults] = useState([]);
  const [accLoading, setAccLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null); // {id, name}

  // ─── Reminder (Bell icon) ────────────────────────────────────────────
  const [remOpen, setRemOpen] = useState(false);
  const [reminder, setReminder] = useState(null); // {label, due_iso, code}

  // ─── Assignee (UserPlus icon) ────────────────────────────────────────
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignQuery, setAssignQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null); // {id, name}

  const accBtnRef = useRef(null);
  const remBtnRef = useRef(null);
  const assignBtnRef = useRef(null);

  // Click-outside to close popovers
  useEffect(() => {
    const onDocClick = (e) => {
      if (accBtnRef.current && !accBtnRef.current.contains(e.target)) setAccOpen(false);
      if (remBtnRef.current && !remBtnRef.current.contains(e.target)) setRemOpen(false);
      if (assignBtnRef.current && !assignBtnRef.current.contains(e.target)) setAssignOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Fetch accounts (debounced)
  useEffect(() => {
    if (!accOpen) return;
    setAccLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/companies`, { params: { search: accQuery, limit: 8 } });
        setAccResults(r.data?.companies || r.data?.data || r.data || []);
      } catch { setAccResults([]); }
      finally { setAccLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [accQuery, accOpen]);

  // Fetch users list once when popover opens
  useEffect(() => {
    if (!assignOpen || users.length > 0) return;
    setUsersLoading(true);
    axios.get(`${API}/users`)
      .then(r => setUsers(r.data || []))
      .catch(() => setUsers([]))
      .finally(() => setUsersLoading(false));
  }, [assignOpen, users.length]);

  const filteredUsers = useMemo(() => {
    const q = assignQuery.trim().toLowerCase();
    if (!q) return users.slice(0, 10);
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    ).slice(0, 10);
  }, [users, assignQuery]);

  // Reminder presets — compute the actual due_iso (date-only) from "today"
  const reminderPresets = useMemo(() => {
    const now = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const d0 = new Date(now); // today
    const d1 = new Date(now); d1.setDate(d1.getDate() + 1);
    const d3 = new Date(now); d3.setDate(d3.getDate() + 3);
    const d7 = new Date(now); d7.setDate(d7.getDate() + 7);
    return [
      { label: 'Today',      due_iso: fmt(d0), code: 'today' },
      { label: 'Tomorrow',   due_iso: fmt(d1), code: 'tomorrow' },
      { label: 'In 3 days',  due_iso: fmt(d3), code: '3d' },
      { label: 'Next week',  due_iso: fmt(d7), code: '1w' },
    ];
  }, []);

  const submit = async () => {
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    try {
      const payload = {
        title: t,
        due_date: reminder?.due_iso || new Date().toISOString().slice(0, 10),
      };
      if (selectedAccount?.id) payload.company_id = selectedAccount.id;
      if (selectedAssignee?.id) payload.assigned_to = selectedAssignee.id;

      const r = await axios.post(`${API}/tasks`, payload);
      // Reset everything
      setTitle('');
      setSelectedAccount(null);
      setSelectedAssignee(null);
      setReminder(null);
      onCreate && onCreate(r.data);
      toast.success('Task created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create task');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative flex items-center gap-2 px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
      <Plus className="w-4 h-4 text-slate-400 shrink-0" />
      <input
        type="text" placeholder="Add a task and press Enter…" value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        disabled={busy}
        className="flex-1 bg-transparent border-0 focus:outline-none text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 disabled:opacity-50"
        data-testid="task-quick-add-input"
      />

      {/* Related Account */}
      <div ref={accBtnRef} className="relative">
        <button
          type="button"
          onClick={() => { setAccOpen(o => !o); setRemOpen(false); setAssignOpen(false); }}
          title="Link to account"
          className={`hidden sm:inline-flex items-center gap-2 text-xs px-2 py-1.5 rounded transition-colors ${selectedAccount ? 'text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}
          data-testid="task-quick-add-account-btn"
        >
          <Building2 className="w-4 h-4" />
          <span className="truncate max-w-[140px]">
            {selectedAccount ? selectedAccount.name : 'Related account…'}
          </span>
          {selectedAccount && (
            <X className="w-3 h-3 ml-1 opacity-70 hover:opacity-100"
               onClick={(e) => { e.stopPropagation(); setSelectedAccount(null); }} />
          )}
        </button>
        {accOpen && (
          <div className="absolute right-0 top-full mt-1.5 w-72 z-30 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden" data-testid="task-quick-add-account-popover">
            <input
              autoFocus
              type="text" placeholder="Search account…"
              value={accQuery} onChange={(e) => setAccQuery(e.target.value)}
              className="w-full px-3 py-2 text-sm border-b border-slate-200 dark:border-slate-700 bg-transparent text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none"
            />
            <div className="max-h-64 overflow-y-auto">
              {accLoading ? (
                <div className="px-3 py-4 text-xs text-slate-400">Searching…</div>
              ) : accResults.length === 0 ? (
                <div className="px-3 py-4 text-xs text-slate-400">No accounts found.</div>
              ) : accResults.map((a) => (
                <button
                  key={a.id}
                  onClick={() => { setSelectedAccount({ id: a.id, name: a.name }); setAccOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 truncate"
                >
                  {a.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reminder */}
      <div ref={remBtnRef} className="relative">
        <button
          type="button"
          onClick={() => { setRemOpen(o => !o); setAccOpen(false); setAssignOpen(false); }}
          title="Set due date"
          className={`p-1.5 rounded transition-colors ${reminder ? 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}
          data-testid="task-quick-add-reminder-btn"
        >
          <Bell className="w-4 h-4" />
        </button>
        {reminder && (
          <span className="absolute -top-1 -right-1 text-[9px] bg-amber-500 text-white px-1 rounded-full">{reminder.code}</span>
        )}
        {remOpen && (
          <div className="absolute right-0 top-full mt-1.5 w-48 z-30 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden" data-testid="task-quick-add-reminder-popover">
            <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700/60 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Due</div>
            {reminderPresets.map((p) => (
              <button
                key={p.code}
                onClick={() => { setReminder(p); setRemOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 ${reminder?.code === p.code ? 'text-blue-600 dark:text-blue-300 font-medium' : 'text-slate-700 dark:text-slate-200'}`}
              >
                {p.label}
              </button>
            ))}
            <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-700/60">
              <input
                type="date"
                value={reminder?.due_iso || ''}
                onChange={(e) => {
                  if (!e.target.value) { setReminder(null); return; }
                  setReminder({ label: 'Custom', due_iso: e.target.value, code: 'custom' });
                }}
                className="w-full px-2 py-1 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-slate-700 dark:text-slate-200 focus:outline-none focus:border-blue-500"
              />
            </div>
            {reminder && (
              <button
                onClick={() => { setReminder(null); setRemOpen(false); }}
                className="w-full px-3 py-2 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 border-t border-slate-100 dark:border-slate-700/60"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Assignee */}
      <div ref={assignBtnRef} className="relative">
        <button
          type="button"
          onClick={() => { setAssignOpen(o => !o); setAccOpen(false); setRemOpen(false); }}
          title="Assign to teammate"
          className={`p-1.5 rounded transition-colors ${selectedAssignee ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}
          data-testid="task-quick-add-assignee-btn"
        >
          <UserPlus className="w-4 h-4" />
        </button>
        {selectedAssignee && (
          <span className="absolute -top-1 -right-1 text-[9px] bg-emerald-600 text-white px-1 rounded-full">{(selectedAssignee.name || '?').charAt(0)}</span>
        )}
        {assignOpen && (
          <div className="absolute right-0 top-full mt-1.5 w-72 z-30 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden" data-testid="task-quick-add-assignee-popover">
            <input
              autoFocus
              type="text" placeholder="Search teammate…"
              value={assignQuery} onChange={(e) => setAssignQuery(e.target.value)}
              className="w-full px-3 py-2 text-sm border-b border-slate-200 dark:border-slate-700 bg-transparent text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none"
            />
            <div className="max-h-64 overflow-y-auto">
              {usersLoading ? (
                <div className="px-3 py-4 text-xs text-slate-400">Loading…</div>
              ) : filteredUsers.length === 0 ? (
                <div className="px-3 py-4 text-xs text-slate-400">No teammates found.</div>
              ) : filteredUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => { setSelectedAssignee({ id: u.id, name: u.name }); setAssignOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2"
                >
                  <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-[11px] font-semibold shrink-0">
                    {(u.name || '?').charAt(0).toUpperCase()}
                  </span>
                  <span className="truncate flex-1">{u.name}</span>
                  <span className="text-[10px] text-slate-400 truncate">{u.email}</span>
                </button>
              ))}
            </div>
            {selectedAssignee && (
              <button
                onClick={() => { setSelectedAssignee(null); setAssignOpen(false); }}
                className="w-full px-3 py-2 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 border-t border-slate-100 dark:border-slate-700/60"
              >
                Unassign
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Manual task row ────────────────────────────────────────────────────
function ManualTaskRow({ t, onComplete }) {
  const fmtDue = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); } catch { return d; }
  };
  return (
    <div className="group flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 border-b border-slate-100 dark:border-slate-700/50 last:border-b-0">
      <button
        onClick={() => onComplete(t)}
        className="w-5 h-5 rounded-full border-2 border-slate-300 dark:border-slate-600 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 shrink-0 transition-colors"
        data-testid={`task-checkbox-${t.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{t.title}</span>
          {t.priority === 'high' && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">
              <AlertCircle className="w-3 h-3" /> High
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2">
          <Clock className="w-3 h-3" /> {fmtDue(t.due_date)}{t.lead_id && ` · Lead`}{t.opportunity_id && ` · Opportunity`}
        </div>
      </div>
      {t.assigned_to_name && <Avatar name={t.assigned_to_name} size={28} />}
      <button className="p-1.5 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 opacity-0 group-hover:opacity-100"><MoreVertical className="w-4 h-4" /></button>
    </div>
  );
}

// ─── Suggested-task row ────────────────────────────────────────────────
function SuggestedRow({ s, isFocused, onAccept, onDismiss, onSnooze, onClick }) {
  const Icon = s.kind === 'idle_lead' ? AlertCircle : Mail;
  const iconColor = s.kind === 'idle_lead' ? 'text-amber-500' : (s.kind === 'follow_up' ? 'text-blue-500' : 'text-slate-400');
  const replyTitle = s.kind === 'idle_lead' ? 'Open lead (R)' : 'Reply to email (R)';
  return (
    <div
      onClick={onClick}
      className={`group flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700/50 last:border-b-0 transition-colors ${isFocused ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}
      data-testid={`suggested-row-${s.key}`}
    >
      <Icon className={`w-4 h-4 shrink-0 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {s.sender && <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 shrink-0 truncate max-w-[200px]">{s.sender}</span>}
          <span className="text-sm text-slate-600 dark:text-slate-300 truncate">{s.title}</span>
          {s.age_days >= 7 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 font-semibold tabular-nums">
              {s.age_days}d
            </span>
          )}
        </div>
        {s.body_preview && <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{s.body_preview}</p>}
      </div>
      {s.account_name && (
        <span className="hidden md:inline text-xs text-slate-500 dark:text-slate-400 truncate max-w-[180px]">
          {s.account_name}
        </span>
      )}
      <div className="flex items-center gap-0.5 shrink-0">
        <button onClick={(e) => { e.stopPropagation(); onAccept(s); }}
                className="p-1.5 rounded text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                title={replyTitle}
                data-testid={`accept-${s.key}`}>
          <Reply className="w-4 h-4" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onSnooze(s); }}
                className="p-1.5 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                title="Snooze (S)">
          <Clock className="w-4 h-4" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDismiss(s); }}
                className="p-1.5 rounded text-slate-400 hover:text-rose-600 dark:hover:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                title="Dismiss (D)"
                data-testid={`dismiss-${s.key}`}>
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────
export default function TasksHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(searchParams.get('tab') || 'today');
  const [tasks, setTasks] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const containerRef = useRef(null);

  // ─── Sync tab → URL ──────────────────────────────────────────────────
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'today') next.delete('tab'); else next.set('tab', tab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ─── Fetch manual tasks (filter by tab) ──────────────────────────────
  const fetchTasks = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tasks`);
      const all = r.data?.tasks || r.data || [];
      const { startISO, endISO } = todayBounds();
      const filtered = all.filter((t) => {
        if (tab === 'completed') return t.completed === true || t.status === 'completed' || t.status === 'done';
        if (t.completed === true || t.status === 'completed') return false;
        const due = t.due_date || t.due_at;
        if (!due) return tab === 'upcoming';
        if (tab === 'today') return due >= startISO.slice(0, 10) && due < endISO.slice(0, 10);
        if (tab === 'upcoming') return due >= endISO.slice(0, 10);
        return true;
      });
      setTasks(filtered);
    } catch { setTasks([]); }
  }, [tab]);

  // ─── Fetch suggested feed ────────────────────────────────────────────
  const fetchSuggested = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tasks-hub/suggested?limit=80`);
      setSuggested(r.data?.data || []);
    } catch { setSuggested([]); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchTasks(), fetchSuggested()]).finally(() => setLoading(false));
  }, [fetchTasks, fetchSuggested]);

  // ─── Apply free-text search filter ───────────────────────────────────
  const visibleSuggested = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suggested;
    return suggested.filter((s) =>
      (s.title || '').toLowerCase().includes(q) ||
      (s.sender || '').toLowerCase().includes(q) ||
      (s.account_name || '').toLowerCase().includes(q)
    );
  }, [suggested, search]);

  const { user } = useAuth();
  const [composeReplyTo, setComposeReplyTo] = useState(null);
  // For idle_lead suggestions we don't have a source email — open compose
  // in `new` mode pre-filled with the lead's contact email + a friendly
  // subject. The leadId enables template variable expansion ({{lead_name}}…).
  const [composeNew, setComposeNew] = useState(null);

  // ─── Suggestion actions ─────────────────────────────────────────────
  const acceptSuggestion = async (s) => {
    if (s.kind === 'idle_lead' && s.source_id) {
      // Fetch lead detail to get the contact email + company name
      try {
        const r = await axios.get(`${API}/leads/${s.source_id}`);
        const lead = r.data || {};
        setComposeNew({
          leadId: lead.id,
          initialData: {
            to: lead.contact_email ? [lead.contact_email] : [],
            subject: lead.company_name ? `Following up on ${lead.company_name}` : 'Following up',
          },
        });
      } catch {
        // Fallback to lead detail page only if the fetch fails
        navigate(`/leads/${s.source_id}`);
      }
      return;
    }
    if (!s.source_id) return;
    // For unreplied / follow_up email suggestions: fetch the original email
    // and open the in-place reply ComposeModal (no page redirect).
    try {
      const r = await axios.get(`${API}/email/${s.source_id}`);
      const email = r.data || {};
      if (!email.id) {
        navigate(`/email?open=${s.source_id}`);
        return;
      }
      setComposeReplyTo(email);
    } catch {
      navigate(`/email?open=${s.source_id}`);
    }
  };
  const dismissSuggestion = async (s) => {
    setSuggested((prev) => prev.filter((x) => x.key !== s.key));
    try { await axios.post(`${API}/tasks-hub/dismiss`, { key: s.key }); }
    catch { toast.error('Dismiss failed'); fetchSuggested(); }
  };
  const snoozeSuggestion = async (s) => {
    setSuggested((prev) => prev.filter((x) => x.key !== s.key));
    try {
      await axios.post(`${API}/tasks-hub/snooze`, { key: s.key, hours: 18 });
      toast.success('Snoozed until tomorrow');
    } catch { toast.error('Snooze failed'); fetchSuggested(); }
  };

  const completeTask = async (t) => {
    try {
      await axios.put(`${API}/tasks/${t.id}`, { completed: true, status: 'completed' });
      toast.success('Marked done');
      fetchTasks();
    } catch { toast.error('Failed to mark done'); }
  };

  // ─── Phase 3: Keyboard shortcuts (J/K/R/D/S) ─────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!visibleSuggested.length) return;
      const focused = visibleSuggested[focusedIdx];
      switch (e.key.toLowerCase()) {
        case 'j': e.preventDefault(); setFocusedIdx((i) => Math.min(i + 1, visibleSuggested.length - 1)); break;
        case 'k': e.preventDefault(); setFocusedIdx((i) => Math.max(i - 1, 0)); break;
        case 'r': if (focused) { e.preventDefault(); acceptSuggestion(focused); } break;
        case 'd': if (focused) { e.preventDefault(); dismissSuggestion(focused); } break;
        case 's': if (focused) { e.preventDefault(); snoozeSuggestion(focused); } break;
        default: break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSuggested, focusedIdx]);

  const totalCount = tasks.length + visibleSuggested.length;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="tasks-hub" ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Tasks</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 tabular-nums">
            {loading ? '…' : `${totalCount} tasks`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <TabStrip tabs={TIME_TABS} value={tab} onChange={setTab} dataTestidPrefix="tasks-time-tab" />
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-8 pr-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 w-44"
              data-testid="tasks-search"
            />
          </div>
        </div>
      </div>

      {/* Quick-add */}
      <div className="mb-5"><QuickAddBar onCreate={fetchTasks} /></div>

      {/* My tasks */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden mb-5 bg-white dark:bg-slate-800" data-testid="my-tasks-card">
        <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
            My tasks · {tasks.length}
          </h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 capitalize">
            {tab === 'today' ? 'Due today' : tab}
          </span>
        </div>
        {loading ? (
          <div className="text-xs text-slate-400 px-4 py-6 text-center">Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="text-xs text-slate-400 px-4 py-6 text-center">
            {tab === 'completed' ? 'No completed tasks' : tab === 'upcoming' ? 'No upcoming tasks. Type above to add one.' : 'Nothing due today. 🎯'}
          </div>
        ) : (
          tasks.map((t) => <ManualTaskRow key={t.id} t={t} onComplete={completeTask} />)
        )}
      </div>

      {/* Suggested */}
      <div className="border border-blue-200 dark:border-blue-900/40 rounded-lg overflow-hidden bg-blue-50/30 dark:bg-blue-900/10" data-testid="suggested-card">
        <div className="px-4 py-2.5 border-b border-blue-200/60 dark:border-blue-900/40 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-900 dark:text-blue-200">Suggested tasks</h3>
            <span className="text-[10px] text-blue-700/70 dark:text-blue-300/70">unreplied emails · idle leads</span>
          </div>
          <span className="text-[10px] text-blue-700/70 dark:text-blue-300/70">{visibleSuggested.length} suggestions · J/K nav · R reply · D dismiss · S snooze</span>
        </div>
        {loading ? (
          <div className="text-xs text-slate-400 px-4 py-6 text-center">Scanning your inbox & leads…</div>
        ) : visibleSuggested.length === 0 ? (
          <div className="text-xs text-slate-400 px-4 py-8 text-center">
            <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
            All caught up. No suggestions.
          </div>
        ) : (
          visibleSuggested.map((s, idx) => (
            <SuggestedRow
              key={s.key}
              s={s}
              isFocused={idx === focusedIdx}
              onClick={() => setFocusedIdx(idx)}
              onAccept={acceptSuggestion}
              onDismiss={dismissSuggestion}
              onSnooze={snoozeSuggestion}
            />
          ))
        )}
      </div>

      <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center mt-4">
        Tip: press <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono">J</kbd> / <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono">K</kbd> to navigate suggestions, <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono">R</kbd> to open, <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono">D</kbd> to dismiss, <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono">S</kbd> to snooze.
      </p>

      {/* In-place Compose modal — opens when user clicks the Reply arrow on
          an unreplied/follow-up suggestion. Stays on /tasks. */}
      {composeReplyTo && (
        <ComposeModal
          mode="reply"
          replyTo={composeReplyTo}
          currentUserEmail={user?.email}
          onClose={() => setComposeReplyTo(null)}
          onSent={() => {
            setComposeReplyTo(null);
            toast.success('Reply sent');
            fetchSuggested();
          }}
        />
      )}

      {/* In-place new-compose for idle leads (no source email to reply to) */}
      {composeNew && (
        <ComposeModal
          mode="new"
          leadId={composeNew.leadId}
          initialData={composeNew.initialData}
          currentUserEmail={user?.email}
          onClose={() => setComposeNew(null)}
          onSent={() => {
            setComposeNew(null);
            toast.success('Email sent');
            fetchSuggested();
          }}
        />
      )}
    </div>
  );
}
